import React, { useEffect, useState, useContext, useRef } from "react";
import { useImmer } from "use-immer";
import AuthContext from "../../connections/auth/AuthProvider";
import cx from "classnames";
import { apiClient } from "../../connections/apiClient";
import ReactSelect from "react-select";
import { ThreeDots } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";
import { toast } from "react-toastify";
import { Slide as ToastSlide } from "react-toastify";
import { onlyLettersAndSpaces } from "../../services/utils/formatAndCompare";
import apiRoutes from "../../connections/apiRoutes";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Tooltip,
} from "@material-ui/core";

// material-ui icons
import { Add, Clear, Close, Group, Save } from "@material-ui/icons";

import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
library.add(faCheck);

// core components
import Button from "../../components/CustomButtons/Button";
import Card from "../../components/Card/Card.js";
import CardBody from "../../components/Card/CardBody.js";
import CardIcon from "../../components/Card/CardIcon.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CustomInput from "../../components/CustomInput/CustomInput";
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import SnackbarContent from "../../components/Snackbar/SnackbarContent";

import {
  cardTitle,
  grayColor,
  whiteColor,
  blendColors,
  dangerColor,
  warningColor,
} from "../../assets/jss/material-dashboard-pro-react";

import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle";
import customSelectStyle from "../../assets/jss/material-dashboard-pro-react/customSelectStyle.js";

const groupsStyle = (theme, clientColor) => ({
  ...modalStyle(theme),
  ...customSelectStyle(theme, clientColor),
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
  },
  cardBody: {
    padding: "0.9375rem 20px",
    flex: "1 1 auto",
    WebkitBoxFlex: "1",
    position: "relative",
  },
  comments: {
    width: "100%",
    marginTop: "12px",
    marginBottom: "12px",
    fontSize: "12px",
    lineHeight: "1.4",
  },
  snackbarPaper: {
    width: "100%",
    marginBottom: "8px",
  },
  snackbarMessage: {
    maxWidth: "100% !important",
  },
  manageGroupsButtons: {
    marginTop: "4px",
    "& > button": {
      paddingLeft: "8px",
      paddingRight: "15px",
      marginRight: "5px",
      "& > span > svg": {
        marginTop: "-2px",
      },
    },
  },
  groupsTable: {
    marginTop: "8px",
    width: "100%",
    fontWeight: "300",
  },
  groupsTableHead: {},
  groupsTableBody: {
    color: grayColor[6],
    "&.saving": {
      opacity: "0.75",
      pointerEvents: "none",
    },
  },
  groupsTableNameCol: {
    width: "22%",
  },
  groupsTableMembersCol: {
    width: "66%",
  },
  groupsTableActionsCol: {
    width: "12%",
    textAlign: "center",
  },
  iconButtonCustom: {
    cursor: "pointer",
    marginLeft: "2px",
    marginRight: "2px",
    transition: "all 0.2s ease-in-out",
    lineHeight: "30px",
    "&.saveButton": {
      height: "30px",
      width: "30px",
      "&:hover": {
        color: clientColor,
      },
      "&.savePending:not(.savingGroup)": {
        color: warningColor[7],
        animation: "$attention 3s ease infinite",
      },
      "&.savingGroup": {
        color: warningColor[7],
        opacity: "0.75",
        animation: "$saving 1s ease-in-out infinite",
        pointerEvents: "none",
      },
      "&:not(.savePending)": {
        color: grayColor[4],
        pointerEvents: "none",
      },
    },
    "&.removeButton": {
      height: "35px",
      width: "35px",
      marginBottom: "-2px",
      "&.saving": {
        color: grayColor[4],
      },
      "&:not(.saving)": {
        "&:hover": {
          color: dangerColor[0],
        },
      },
    },
  },
  selectFormControl: {
    paddingTop: "7px",
    margin: "7px 0 7px 0 !important",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  "@keyframes attention": {
    "10%": {
      transform: "scale(1.2)",
    },
    "13.33%,20%": {
      transform: "rotate(-10deg) scale(1.2)",
    },
    "16.67%": {
      transform: "rotate(10deg) scale(1.2)",
    },
    "23.33%": {
      transform: "rotate(0deg) scale(1.2)",
    },
    "33%": {
      transform: "scale(1)",
    },
  },
  "@keyframes saving": {
    "0%": {
      opacity: "0.75",
    },
    "50%": {
      opacity: "0.25",
    },
    "100%": {
      opacity: "0.75",
    },
  },
});

export default function Groups() {
  /* region initial declarations */
  let { userDetails } = useContext(AuthContext);
  const client_styles = (theme) => ({
    ...groupsStyle(theme, userDetails.implementation_color),
  });
  const useStyles = makeStyles(client_styles);
  const classes = useStyles();

  const T_PREFIX = "global.admin.groups";
  const { t, i18n } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const notify = (message, type = "info", autoClose = 3000) =>
    toast(message, {
      position: "top-right",
      autoClose: autoClose,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "light",
      type: type,
      transition: ToastSlide,
    });
  const loadingSpinner = () => {
    return (
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <ThreeDots
          width="100px"
          height="100px"
          color={userDetails.implementation_color}
          ariaLabel="loading"
          wrapperStyle={{}}
          wrapperClass=""
        />
      </div>
    );
  };
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "0",
      borderWidth: "0 0 1px 0",
      borderColor: state.isFocused
        ? userDetails.implementation_color
        : provided.borderColor,
      marginTop: "-7px",
      boxShadow: "none",
      "&:hover": {
        borderColor: userDetails.implementation_color,
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? blendColors(whiteColor, userDetails.implementation_color, 0.8) // Over mouse color on Dropdown items
        : whiteColor, // Color of the list not selected on Dropdown
      "&:active": {
        backgroundColor: blendColors(
          whiteColor,
          userDetails.implementation_color,
          0.7,
        ),
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: blendColors(
        whiteColor,
        userDetails.implementation_color,
        0.8,
      ), // Background color of selected tags
    }),
  };
  /* endregion */

  /* region state declarations */
  const [dataLoaded, setDataLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingGroup, setSavingGroup] = useState(null);
  const [groupToExclude, setGroupToExclude] = useState(null);
  const [groupToExcludeName, setGroupToExcludeName] = useState(null);

  const [usersData, setUsersData] = useState([]);
  const [groupsData, setGroupsData] = useImmer([]);
  const nameInputsRef = useRef([]);
  /* endregion */

  /* region useEffect's */
  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);
  useEffect(() => {
    apiClient
      .post(apiRoutes.adminview_groups)
      .then((response) => {
        setUsersData(response.data.users);
        let groupsResponseData = structuredClone(response.data.groups);
        for (let i = 0; i < groupsResponseData.length; i++) {
          groupsResponseData["save_pending"] = false;
        }
        setGroupsData(groupsResponseData);
        setDataLoaded(true);
      })
      .catch((error) => {
        const error_code = error.response.data?.error_code;
        notify(
          error_code
            ? t(`errors.${error_code}`)
            : "Error communicating with the server, please try again",
          "error",
        );
        setDataLoaded(true);
        console.log(error);
      });
  }, []);
  /* endregion */

  /* region handlers */
  const handleAddGroupButtonClick = () => {
    if (saving) return false;
    setGroupsData((draft) => {
      draft.push({
        group_id: "0",
        group_name: "",
        members: [],
        save_pending: true,
      });
    });
    setTimeout(
      () =>
        nameInputsRef.current[
          nameInputsRef.current.length - 1
        ].firstChild.focus(),
      200,
    );
  };
  const handleSaveGroupButtonClick = (pos) => {
    if (saving) return false;
    if (!groupsData[pos].save_pending) return false;
    if (groupsData[pos].group_name === "") {
      notify(t("errors.no_name"), "error");
      return false;
    }
    if (groupsData[pos].members.length === 0) {
      notify(t("errors.no_members"), "error");
      return false;
    }
    const params = {
      action: "save",
      group_data: {
        group_id: groupsData[pos].group_id,
        group_name: groupsData[pos].group_name,
        members: groupsData[pos].members.map((m) => m.id),
      },
    };
    setSaving(true);
    setSavingGroup(pos);
    apiClient
      .post(apiRoutes.adminview_groups_save, params)
      .then((response) => {
        setGroupsData((draft) => {
          if (draft[pos].group_id === "0") {
            draft[pos].group_id = response.data.group_id;
          }
          draft[pos].group_name = response.data.group_name;
          draft[pos].save_pending = false;
        });
        setSaving(false);
        setSavingGroup(null);
        notify(t("save_success"), "success", 2000);
      })
      .catch((error) => {
        const error_code = error.response.data?.error_code;
        notify(
          error_code
            ? t(`errors.${error_code}`)
            : "Error communicating with the server, please try again",
          "danger",
        );
        setSaving(false);
        setSavingGroup(null);
      });
  };
  const handleRemoveGroupButtonClick = (pos) => {
    if (saving) return false;
    if (
      groupsData[pos]["group_id"] === "0" &&
      (groupsData[pos]["group_name"] === "" ||
        groupsData[pos]["members"].length === 0)
    ) {
      setGroupsData((draft) => {
        draft.splice(pos, 1);
      });
    } else {
      setGroupToExcludeName(groupsData[pos]["group_name"]);
      setGroupToExclude(pos);
    }
  };
  const handleConfirmRemoveGroupButtonClick = () => {
    if (saving) return false;
    const notifySuccess = () => notify(t("removal_success"), "success");
    if (groupsData[groupToExclude]["group_id"] === "0") {
      setGroupsData((draft) => {
        draft.splice(groupToExclude, 1);
      });
      setGroupToExclude(null);
      notifySuccess();
    } else {
      const params = {
        action: "delete",
        group_data: {
          group_id: groupsData[groupToExclude]["group_id"],
        },
      };
      setSaving(true);
      apiClient
        .post(apiRoutes.adminview_groups_save, params)
        .then(() => {
          setGroupsData((draft) => {
            draft.splice(groupToExclude, 1);
          });
          setGroupToExclude(null);
          setSaving(false);
          notifySuccess();
        })
        .catch((error) => {
          let error_code = error.response.data?.error_code || "server_error";
          notify(t(`errors.${error_code}`), "error");
          setSaving(false);
          setGroupToExclude(null);
        });
    }
  };
  const handleMembersSelectionChange = (e, pos) => {
    if (saving) return false;
    setGroupsData((draft) => {
      let members = [];
      for (let i = 0; i < e.length; i++) {
        members.push({
          id: e[i].value,
          name: e[i].label,
        });
      }
      draft[pos].members = members;
      draft[pos].save_pending = true;
    });
  };
  const handleGroupNameChange = (e, pos) => {
    if (saving) return false;
    setGroupsData((draft) => {
      draft[pos].group_name = onlyLettersAndSpaces(e.target.value);
      draft[pos].save_pending = true;
    });
  };
  const handleGroupNameBlur = (e, pos) => {
    setGroupsData((draft) => {
      draft[pos]["group_name"] = draft[pos]["group_name"]
        .trim()
        .replace(/\s+/g, " ");
    });
  };
  /* endregion */

  /* region components */
  const confirmExclusionDialog = (
    <Dialog
      classes={{
        root: classes.modalRoot,
        paper: classes.modal,
      }}
      open={groupToExclude !== null}
      onClose={() => setGroupToExclude(null)}
      aria-labelledby="classic-modal-slide-title"
      aria-describedby="classic-modal-slide-description"
      width={"sm"}
    >
      <DialogTitle
        id="content-modal-slide-title"
        disableTypography
        className={cx({
          [classes.modalHeader]: true,
        })}
      >
        <Button
          justIcon
          className={classes.modalCloseButton}
          key="close"
          aria-label="Close"
          color="transparent"
          onClick={() => setGroupToExclude(null)}
          disabled={saving}
        >
          <Close className={classes.modalClose} />
        </Button>
        <h4 className={classes.modalTitle}>
          <span className={classes.modalHeader}>{t("remove_group")}</span>
        </h4>
      </DialogTitle>
      <DialogContent
        id="classic-modal-slide-description"
        className={classes.modalBody}
      >
        {`${t("confirm_removal")} '${groupToExcludeName}'`}
      </DialogContent>
      <DialogActions className={classes.modalFooter}>
        <Button
          onClick={() => handleConfirmRemoveGroupButtonClick()}
          color="danger"
          simple
          disabled={saving}
        >
          {!saving && t("global.ok")}
          {saving && (
            <ThreeDots
              color={dangerColor[0]}
              ariaLabel="saving"
              width={"20px"}
              height={"12px"}
              // wrapperStyle={{}}
              // wrapperClass={classes.plSpinner}
            />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
  /* endregion */

  /* region return */
  return (
    <div>
      <GridContainer style={{ marginTop: "-40px" }}>
        <GridItem xs={12}>
          <div>
            <Card>
              <CardHeader color="client" icon>
                <CardIcon color="client">
                  <Group />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>{t("groups")}</h4>
              </CardHeader>
              <CardBody className={classes.cardBody}>
                <GridContainer justifyContent="flex-start">
                  {dataLoaded && Object.keys(usersData).length > 0 && (
                    <>
                      {/* region comments */}
                      <GridItem
                        sm={12}
                        className={cx({
                          [classes.comments]: true,
                        })}
                      >
                        {t("utilization")}
                        {":"}
                        {[...Array(2).keys()].map((key, val) => {
                          return (
                            <React.Fragment key={key}>
                              <br />
                              <span>
                                {"• "}
                                {t(`comments.${val + 1}`)}
                              </span>
                            </React.Fragment>
                          );
                        })}
                      </GridItem>
                      {/* endregion */}
                      {/* region groups table */}
                      <GridItem md={12} lg={8} xl={6}>
                        <table className={classes.groupsTable}>
                          <thead className={classes.groupsTableHead}>
                            <tr>
                              <td className={classes.groupsTableNameCol}>
                                {"Group name"}
                              </td>
                              <td className={classes.groupsTableMembersCol}>
                                {"Members"}
                              </td>
                              <td className={classes.groupsTableActionsCol}>
                                {"Actions"}
                              </td>
                            </tr>
                          </thead>
                          <tbody
                            className={cx({
                              [classes.groupsTableBody]: true,
                              ["saving"]: saving,
                            })}
                          >
                            {groupsData.map((group, key) => {
                              return (
                                <tr key={key}>
                                  <td className={classes.groupsTableNameCol}>
                                    <CustomInput
                                      id={"group_name_" + key}
                                      ref={(e) =>
                                        (nameInputsRef.current[key] = e)
                                      }
                                      value={group.group_name}
                                      placeholder={t("group_name")}
                                      onChange={(e) =>
                                        handleGroupNameChange(e, key)
                                      }
                                      onBlur={(e) =>
                                        handleGroupNameBlur(e, key)
                                      }
                                      formControlProps={{
                                        style: {
                                          paddingTop: "7px",
                                          // paddingTop: "0",
                                          paddingBottom: "0",
                                          paddingRight: "6px",
                                          marginTop: "-2px",
                                          marginBottom: "0",
                                          width: "100%",
                                        },
                                      }}
                                      inputProps={{
                                        type: "text",
                                        autoComplete: "off",
                                        disabled: false,
                                        maxLength: "25",
                                      }}
                                      client
                                    />
                                  </td>
                                  <td className={classes.groupsTableMembersCol}>
                                    <FormControl
                                      fullWidth
                                      className={classes.selectFormControl}
                                      style={{
                                        margin: "7px 0 7px 0 !important",
                                      }}
                                    >
                                      <ReactSelect
                                        isMulti
                                        closeMenuOnSelect={false}
                                        options={usersData.map((user) => ({
                                          value: user.id,
                                          label: user.name,
                                        }))}
                                        value={group.members.map((user) => ({
                                          value: user.id,
                                          label: user.name,
                                        }))}
                                        onChange={(e) =>
                                          handleMembersSelectionChange(e, key)
                                        }
                                        placeholder={t("global.select") + "..."}
                                        styles={{
                                          ...selectStyles,
                                        }}
                                      />
                                    </FormControl>
                                  </td>
                                  <td className={classes.groupsTableActionsCol}>
                                    <Tooltip
                                      title={t("save")}
                                      arrow
                                      enterDelay={500}
                                    >
                                      <Save
                                        className={cx({
                                          [classes.iconButtonCustom]: true,
                                          ["saveButton"]: true,
                                          ["savePending"]:
                                            group.save_pending &&
                                            group.group_name !== "" &&
                                            group.members.length > 0,
                                          ["savingGroup"]: savingGroup === key,
                                        })}
                                        onClick={() =>
                                          handleSaveGroupButtonClick(key)
                                        }
                                        size="large"
                                      />
                                    </Tooltip>
                                    <Tooltip
                                      title={t("remove_group")}
                                      arrow
                                      enterDelay={500}
                                    >
                                      <Clear
                                        className={cx({
                                          [classes.iconButtonCustom]: true,
                                          ["removeButton"]: true,
                                          ["saving"]: saving,
                                        })}
                                        onClick={() =>
                                          handleRemoveGroupButtonClick(key)
                                        }
                                      />
                                    </Tooltip>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </GridItem>
                      {/* endregion */}
                      {/* region no groups created */}
                      {Object.keys(groupsData).length === 0 && (
                        <SnackbarContent
                          message={t("no_groups_created")}
                          color="successLight"
                          paperClass={classes.snackbarPaper}
                          messageClass={classes.snackbarMessage}
                        />
                      )}
                      {/* endregion */}
                      {/* region add group button */}
                      <GridItem
                        sm={12}
                        className={cx({
                          [classes.manageGroupsButtons]: true,
                        })}
                      >
                        <Button
                          aria-label="add_group"
                          color="client"
                          size={"sm"}
                          onClick={() => handleAddGroupButtonClick()}
                          disabled={saving}
                        >
                          <Add />
                          {t("add_group")}
                        </Button>
                        {saving && (
                          <div
                            style={{
                              display: "inline-block",
                              marginLeft: "5px",
                            }}
                          >
                            <ThreeDots
                              width="30px"
                              height="10px"
                              color={userDetails.implementation_color}
                              ariaLabel="loading"
                              wrapperStyle={{}}
                              wrapperClass=""
                            />
                          </div>
                        )}
                      </GridItem>
                      {/* endregion */}
                    </>
                  )}
                  {!dataLoaded && loadingSpinner()}
                  {dataLoaded && Object.keys(usersData).length === 0 && (
                    <SnackbarContent
                      message={t("error_on_loading")}
                      color="error"
                      paperClass={classes.snackbarPaper}
                      messageClass={classes.snackbarMessage}
                    />
                  )}
                </GridContainer>
              </CardBody>
            </Card>
          </div>
        </GridItem>
      </GridContainer>
      {confirmExclusionDialog}
    </div>
  );
  /* endregion */
}
